import { typeboxResolver } from '@hookform/resolvers/typebox';
import { DialogTrigger } from '@radix-ui/react-dialog';
import { Static, Type } from '@sinclair/typebox';
import { useMutation } from '@tanstack/react-query';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { showErrorToast, toast } from '@/components/ui/use-toast';
import { flowsApi } from '@/features/flows/lib/flows-api';
import { FlowOperationType, PopulatedFlow } from '@activepieces/shared';

const MAX_LENGTH = 250;

const RenameFlowSchema = Type.Object({
  displayName: Type.String({ 
    maxLength: MAX_LENGTH,
    errorMessage: t('Flow name must be {count} characters or less', {
        count: MAX_LENGTH,
      })
  }),
});

type RenameFlowSchema = Static<typeof RenameFlowSchema>;

type RenameFlowDialogProps = {
  children: React.ReactNode;
  flowId: string;
  currentFlowName: string;
  onRename: (newName: string) => void;
};
const RenameFlowDialog: React.FC<RenameFlowDialogProps> = ({
  children,
  flowId,
  currentFlowName,
  onRename,
}) => {
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const renameFlowForm = useForm<RenameFlowSchema>({
    resolver: typeboxResolver(RenameFlowSchema),
  });

  useEffect(() => {
    if (isRenameDialogOpen) {
      renameFlowForm.reset({
        displayName: currentFlowName === 'Untitled' ? '' : currentFlowName,
      });
    }
  }, [isRenameDialogOpen, currentFlowName, renameFlowForm]);

  const { mutate, isPending } = useMutation<
    PopulatedFlow,
    Error,
    {
      flowId: string;
      displayName: string;
    }
  >({
    mutationFn: (variables) =>
      flowsApi.update(flowId, {
        type: FlowOperationType.CHANGE_NAME,
        request: { displayName: variables.displayName },
      }),
    onSuccess: (data, variables) => {
      setIsRenameDialogOpen(false);
      onRename(variables.displayName);
      toast({
        variant: 'success',
        title: t('Success'),
        description: t('Flow has been renamed.'),
        duration: 3000,
      });
    },
    onError: (error) => showErrorToast(error),
  });

  return (
    <Dialog
      open={isRenameDialogOpen}
      onOpenChange={(open) => setIsRenameDialogOpen(open)}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('Rename Flow')}</DialogTitle>
        </DialogHeader>
        <Form {...renameFlowForm}>
          <form
            className="grid space-y-4"
            onSubmit={renameFlowForm.handleSubmit((data) =>
              mutate({
                flowId,
                displayName: data.displayName.trim() === '' ? 'Untitled' : data.displayName,
              }),
            )}
          >
            <FormField
              control={renameFlowForm.control}
              name="displayName"
              render={({ field }) => (
                <FormItem className="grid space-y-2">
                  <Label htmlFor="displayName">{t('Name')}</Label>
                  <Input
                    {...field}
                    id="displayName"
                    placeholder={t('New Flow Name')}
                    className="rounded-sm"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            {renameFlowForm?.formState?.errors?.root?.serverError && (
              <FormMessage>
                {renameFlowForm.formState.errors.root.serverError.message}
              </FormMessage>
            )}
            <Button loading={isPending}>{t('Confirm')}</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export { RenameFlowDialog };
